<template>
  <div class="vlm-test" tabindex="0">
    <div class="container">
      <h1>VLM 이미지 분석 테스트</h1>
      
      <!-- 모델 정보 -->
      <div class="model-info">
        <h3>WebNN VLM Test</h3>
        <ul>
          <li>model : llava-onevision-qwen2-0.5b-ov-hf</li>
          <li>HuggingFace Transformers.js를 사용한 Vision Language Model</li>
          <li>WebGPU 가속을 통한 로컬 이미지 분석</li>
        </ul>
      </div>
      
      <!-- 이미지 업로드 섹션 -->
      <div class="upload-section">
        <h2>🖼️ 이미지 업로드</h2>
        <div class="image-upload-area" @drop="handleImageDrop" @dragover.prevent @dragenter.prevent>
          <input 
            ref="imageInput" 
            type="file" 
            accept="image/*" 
            @change="handleImageSelect" 
            style="display: none"
          />
          <div v-if="!selectedImage" class="upload-placeholder">
            <button @click="$refs.imageInput.click()" class="image-upload-btn">
              📷 이미지 선택
            </button>
            <p>또는 이미지를 여기로 드래그하세요</p>
            <p class="supported-formats">지원 형식: JPG, PNG, GIF, WebP</p>
          </div>
          <div v-else class="image-preview">
            <img :src="imagePreviewUrl" alt="Preview" class="preview-image" />
            <div class="image-actions">
              <button @click="clearImage" class="clear-image-btn">🗑️ 이미지 제거</button>
              <button @click="$refs.imageInput.click()" class="change-image-btn">🔄 이미지 변경</button>
            </div>
          </div>
        </div>
        
        <div v-if="selectedImage" class="analysis-controls">
          <button 
            @click="analyzeImage" 
            :disabled="isAnalyzing || isModelLoading"
            class="analyze-btn"
          >
            {{ isAnalyzing ? '분석 중...' : isModelLoading ? '모델 로딩 중...' : '🔍 이미지 분석 시작' }}
          </button>
        </div>
      </div>

      <!-- 분석 결과 섹션 -->
      <div v-if="analysisResult" class="results-section">
        <div class="results-header">
          <h2>📝 분석 결과</h2>
          <div class="result-actions">
            <button @click="speakAnalysis" :disabled="isSpeaking" class="speak-btn">
              {{ isSpeaking ? '🔊 재생 중...' : '🔊 음성으로 듣기' }}
            </button>
            <button @click="stopSpeaking" v-if="isSpeaking" class="stop-speak-btn">
              ⏹️ 중지
            </button>
            <button @click="copyToClipboard" class="copy-btn">
              📋 복사
            </button>
          </div>
        </div>
        <div class="analysis-result">
          <p>{{ analysisResult }}</p>
        </div>
      </div>

      <!-- 로딩 상태 -->
      <div v-if="isModelLoading || isAnalyzing" class="loading">
        <div class="spinner"></div>
        <p>{{ loadingMessage }}</p>
      </div>

      <!-- 분석 히스토리 -->
      <div class="history-section" v-if="analysisHistory.length > 0">
        <div class="history-header">
          <h2>📚 분석 히스토리 ({{ analysisHistory.length }}개)</h2>
          <button @click="clearHistory" class="clear-history-btn">전체 삭제</button>
        </div>
        <div class="history-list">
          <div 
            v-for="item in analysisHistory" 
            :key="item.id" 
            class="history-item"
          >
            <div class="history-header-item">
              <span class="history-time">{{ item.timestamp }}</span>
              <div class="history-actions">
                <button @click="speakHistoryItem(item.result)" class="speak-history-btn" title="음성으로 듣기">
                  🔊
                </button>
                <button @click="deleteHistoryItem(item.id)" class="delete-history-btn" title="삭제">
                  🗑️
                </button>
              </div>
            </div>
            <div class="history-image">
              <img :src="item.imageUrl" alt="History image" class="history-thumbnail" />
            </div>
            <div class="history-result">{{ item.result }}</div>
          </div>
        </div>
      </div>

      <!-- 오류 메시지 -->
      <div v-if="error" class="error">
        <p>{{ error }}</p>
        <button @click="clearError" class="clear-btn">확인</button>
      </div>
    </div>
  </div>
</template>

<script>
import { generateImageCaption, speakText, stopSpeaking } from '@/js/VLM.js';

export default {
  name: 'VlmTest',
  data() {
    return {
      selectedImage: null,
      imagePreviewUrl: null,
      analysisResult: '',
      isAnalyzing: false,
      isModelLoading: false,
      isSpeaking: false,
      error: null,
      loadingMessage: '',
      analysisHistory: [],
      currentUtterance: null
    }
  },
  mounted() {
    this.loadAnalysisHistory();
  },
  methods: {
    handleImageSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.processImageFile(file);
      }
    },

    handleImageDrop(event) {
      event.preventDefault();
      const file = event.dataTransfer.files[0];
      if (file && file.type.startsWith('image/')) {
        this.processImageFile(file);
      } else {
        this.error = '이미지 파일만 업로드할 수 있습니다.';
      }
    },

    processImageFile(file) {
      this.selectedImage = file;
      this.imagePreviewUrl = URL.createObjectURL(file);
      this.analysisResult = '';
      this.error = null;
      console.log('이미지 선택됨:', file.name);
    },

    clearImage() {
      if (this.imagePreviewUrl) {
        URL.revokeObjectURL(this.imagePreviewUrl);
      }
      this.selectedImage = null;
      this.imagePreviewUrl = null;
      this.analysisResult = '';
      this.error = null;
    },

    async analyzeImage() {
      if (!this.selectedImage) {
        this.error = '분석할 이미지를 선택해주세요.';
        return;
      }

      try {
        this.isAnalyzing = true;
        this.loadingMessage = 'VLM 모델로 이미지를 분석 중입니다...';
        this.error = null;

        // 이미지 URL 생성 (이미 생성되어 있지만 확실히 하기 위해)
        const imageUrl = this.imagePreviewUrl || URL.createObjectURL(this.selectedImage);

        console.log('🖼️ 이미지 분석 시작:', imageUrl);
        
        const result = await generateImageCaption(imageUrl);
        
        this.analysisResult = result;
        console.log('📝 분석 완료:', result);

        // 히스토리에 추가
        await this.saveToHistory(imageUrl, result);
        
      } catch (error) {
        console.error('❌ 이미지 분석 오류:', error);
        this.error = '이미지 분석 중 오류가 발생했습니다: ' + error.message;
      } finally {
        this.isAnalyzing = false;
      }
    },

    async speakAnalysis() {
      if (!this.analysisResult) {
        this.error = '음성으로 읽을 분석 결과가 없습니다.';
        return;
      }

      try {
        this.isSpeaking = true;
        this.currentUtterance = speakText(this.analysisResult);
        
        // TTS 완료 감지
        if (this.currentUtterance) {
          this.currentUtterance.onend = () => {
            this.isSpeaking = false;
          };
          this.currentUtterance.onerror = () => {
            this.isSpeaking = false;
            this.error = 'TTS 재생 중 오류가 발생했습니다.';
          };
        }
      } catch (error) {
        console.error('❌ TTS 오류:', error);
        this.error = 'TTS 기능 실행 중 오류가 발생했습니다: ' + error.message;
        this.isSpeaking = false;
      }
    },

    stopSpeaking() {
      stopSpeaking();
      this.isSpeaking = false;
    },

    async copyToClipboard() {
      if (!this.analysisResult) {
        this.error = '복사할 내용이 없습니다.';
        return;
      }

      try {
        await navigator.clipboard.writeText(this.analysisResult);
        // 간단한 복사 완료 알림
        const originalResult = this.analysisResult;
        this.analysisResult = '📋 클립보드에 복사되었습니다! ' + originalResult;
        
        setTimeout(() => {
          this.analysisResult = originalResult;
        }, 2000);
      } catch (error) {
        console.error('❌ 클립보드 복사 오류:', error);
        this.error = '클립보드 복사에 실패했습니다.';
      }
    },

    async saveToHistory(imageUrl, result) {
      const historyItem = {
        id: Date.now(),
        imageUrl: imageUrl,
        result: result,
        timestamp: new Date().toLocaleString('ko-KR')
      };

      this.analysisHistory.unshift(historyItem);

      // 히스토리 개수 제한 (최대 10개)
      if (this.analysisHistory.length > 10) {
        this.analysisHistory = this.analysisHistory.slice(0, 10);
      }

      try {
        localStorage.setItem('vlm-analysis-history', JSON.stringify(this.analysisHistory));
        console.log('💾 분석 결과 히스토리에 저장됨');
      } catch (error) {
        console.error('❌ 히스토리 저장 실패:', error);
      }
    },

    loadAnalysisHistory() {
      try {
        const saved = localStorage.getItem('vlm-analysis-history');
        if (saved) {
          this.analysisHistory = JSON.parse(saved);
        }
      } catch (error) {
        console.error('❌ 히스토리 로드 실패:', error);
      }
    },

    speakHistoryItem(text) {
      try {
        speakText(text);
      } catch (error) {
        console.error('❌ 히스토리 TTS 오류:', error);
        this.error = 'TTS 재생에 실패했습니다.';
      }
    },

    deleteHistoryItem(id) {
      this.analysisHistory = this.analysisHistory.filter(item => item.id !== id);
      try {
        localStorage.setItem('vlm-analysis-history', JSON.stringify(this.analysisHistory));
        console.log('🗑️ 히스토리 항목 삭제됨:', id);
      } catch (error) {
        console.error('❌ 히스토리 삭제 실패:', error);
      }
    },

    clearHistory() {
      if (confirm('모든 분석 히스토리를 삭제하시겠습니까?')) {
        this.analysisHistory = [];
        try {
          localStorage.removeItem('vlm-analysis-history');
          console.log('🗑️ 모든 히스토리 삭제됨');
        } catch (error) {
          console.error('❌ 전체 히스토리 삭제 실패:', error);
        }
      }
    },

    clearError() {
      this.error = null;
    }
  }
}
</script>

<style scoped>
.vlm-test {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
}

.container {
  max-width: 900px;
  margin: 0 auto;
  background: white;
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

h1 {
  text-align: center;
  color: #333;
  margin-bottom: 30px;
  font-size: 2.5em;
}

h2 {
  color: #555;
  margin-bottom: 20px;
  border-bottom: 2px solid #667eea;
  padding-bottom: 10px;
}

.model-info {
  background: #f0f4ff;
  padding: 20px;
  border-radius: 10px;
  margin-bottom: 30px;
  border-left: 5px solid #667eea;
}

.model-info h3 {
  color: #667eea;
  margin-bottom: 15px;
}

.model-info ul {
  list-style-type: disc;
  padding-left: 20px;
}

.model-info li {
  margin-bottom: 5px;
  color: #555;
}

.upload-section, .results-section, .history-section {
  margin-bottom: 30px;
  padding: 20px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  background: #f9f9f9;
}

.image-upload-area {
  border: 2px dashed #667eea;
  border-radius: 10px;
  padding: 40px;
  text-align: center;
  transition: all 0.3s ease;
  cursor: pointer;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.image-upload-area:hover {
  border-color: #764ba2;
  background: #f0f4ff;
}

.upload-placeholder p {
  margin: 10px 0;
  color: #666;
}

.supported-formats {
  font-size: 12px;
  color: #999;
}

.image-upload-btn {
  background: linear-gradient(45deg, #667eea, #764ba2);
  color: white;
  border: none;
  padding: 15px 30px;
  border-radius: 25px;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.3s ease;
  margin-bottom: 15px;
}

.image-upload-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

.image-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.preview-image {
  max-width: 100%;
  max-height: 400px;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.image-actions {
  display: flex;
  gap: 10px;
}

.clear-image-btn, .change-image-btn {
  background: #ff9800;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
}

.clear-image-btn:hover, .change-image-btn:hover {
  transform: translateY(-1px);
}

.clear-image-btn {
  background: #f44336;
}

.clear-image-btn:hover {
  background: #d32f2f;
}

.change-image-btn:hover {
  background: #f57c00;
}

.analysis-controls {
  text-align: center;
  margin-top: 20px;
}

.analyze-btn {
  background: linear-gradient(45deg, #4caf50, #45a049);
  color: white;
  border: none;
  padding: 15px 30px;
  border-radius: 25px;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.3s ease;
}

.analyze-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

.analyze-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 10px;
}

.result-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.speak-btn, .stop-speak-btn, .copy-btn {
  background: #2196f3;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
}

.speak-btn:hover, .copy-btn:hover {
  background: #1976d2;
  transform: translateY(-1px);
}

.stop-speak-btn {
  background: #f44336;
}

.stop-speak-btn:hover {
  background: #d32f2f;
}

.speak-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.analysis-result {
  background: white;
  padding: 20px;
  border-radius: 10px;
  border-left: 4px solid #4caf50;
  font-size: 16px;
  line-height: 1.6;
  min-height: 60px;
}

.loading {
  text-align: center;
  padding: 30px;
  background: #f0f4ff;
  border-radius: 10px;
  margin: 20px 0;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.clear-history-btn {
  background: #ff5722;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
}

.clear-history-btn:hover {
  background: #d84315;
  transform: translateY(-1px);
}

.history-list {
  max-height: 500px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: white;
}

.history-item {
  padding: 15px;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s ease;
}

.history-item:last-child {
  border-bottom: none;
}

.history-item:hover {
  background: #f8f9fa;
}

.history-header-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.history-time {
  font-size: 12px;
  color: #666;
  background: #e9ecef;
  padding: 4px 8px;
  border-radius: 12px;
}

.history-actions {
  display: flex;
  gap: 5px;
}

.speak-history-btn, .delete-history-btn {
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.speak-history-btn:hover {
  background: #e3f2fd;
}

.delete-history-btn:hover {
  background: #ffebee;
}

.history-image {
  margin-bottom: 10px;
}

.history-thumbnail {
  max-width: 100px;
  max-height: 100px;
  border-radius: 5px;
  object-fit: cover;
}

.history-result {
  color: #333;
  line-height: 1.5;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 6px;
  border-left: 3px solid #667eea;
}

.error {
  background: #ffebee;
  color: #c62828;
  padding: 20px;
  border-radius: 10px;
  border-left: 4px solid #f44336;
  margin: 20px 0;
}

.clear-btn {
  background: #f44336;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 5px;
  cursor: pointer;
  margin-top: 10px;
}

.clear-btn:hover {
  background: #d32f2f;
}

@media (max-width: 768px) {
  .container {
    padding: 15px;
    margin: 10px;
  }
  
  h1 {
    font-size: 2em;
  }
  
  .image-upload-area {
    padding: 20px;
    min-height: 150px;
  }
  
  .results-header {
    flex-direction: column;
    align-items: stretch;
  }
  
  .result-actions {
    justify-content: center;
  }
  
  .history-header {
    flex-direction: column;
    gap: 10px;
    align-items: stretch;
  }
  
  .history-list {
    max-height: 400px;
  }
  
  .history-header-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .preview-image {
    max-height: 300px;
  }
}
</style>
